<!-- PhoneInput.vue -->
<script lang="ts" setup>
import TextInput from '../TextInput.vue';
import { LimitToPhoneCharacters } from '../../lib/Validation';
import { IconTypes, DisplayStyle } from "../../Enums/UiEnums"
import Icon from '../Icon.vue';
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue';
import { ValidationState } from '../../lib/Types/Types';

    defineExpose({ focus });  

    const textInput = ref();

    const props = withDefaults(defineProps<{
        modelValue: string|undefined,   // Use with v-model
        title?: string,
        disabled?: boolean,
        invalidValueText?: string
    }>(), {
    });

    const compData = reactive<{
        hasFocus?: boolean
    }>({});

    const emit = defineEmits<{
        (e: "update:model-value", strValue: string|undefined): void
        (e: "validationState", state: ValidationState, msg?: string): void,
    }>();

    const iconDisplayStyle = computed<DisplayStyle>(() => props.disabled ? DisplayStyle.Dark : DisplayStyle.Primary);

    const justNumbers = computed<string|undefined>(() => props.modelValue?.replace(/\D/g, ''));
    const badNumber = computed<boolean>(() => ((justNumbers.value?.length ?? 0) > 0) && !validNumber.value);
    const indicateBadNumber = computed<boolean>(() => !compData.hasFocus && badNumber.value);

    const validNumber = computed<boolean>(() => {
        if (10 == (justNumbers.value?.length ?? 0))
            return true;

        return false;
    });

    const rightAddonDisplayStyle = computed(() =>{
        if (badNumber.value)
            return DisplayStyle.Danger;
        if (validNumber.value)
            return DisplayStyle.Success;
        return;
    });

    function formattedText(): string | undefined
    {
        if (!validNumber.value)
        {
            return props.modelValue;
        }

        const numbers = justNumbers.value ?? "";
        if (10 === numbers.length)
        {
            return `(${numbers.substring(0, 3)}) ${numbers.substring(3, 6)}-${numbers.substring(6, 10)}`;
        }

        // Isn't 10 digits so don't format
        return props.modelValue;
    }

    function emitFormattedText()
    {
        compData.hasFocus = false;
        emit('update:model-value', formattedText());
        PerformValidation();
    }

    function focus()
    {
        textInput.value?.focus();
    }

    function onFocus(evt: FocusEvent)
    {
        compData.hasFocus = true;
    }

    function PerformValidation()
    {
        emit("validationState", !badNumber.value, props.invalidValueText ?? "Invalid phone number"); 
    }

    onMounted(()=>{
        if (props.modelValue?.length)
        {
            PerformValidation();
            return;
        }
    });

    onUnmounted(() => {
        emit("validationState", "unmounted");
    });

    function onUpdateValue(val: string|number|undefined): void
    {
        emit('update:model-value', val?.toString());

        // Wait for the property to update before validating
        nextTick(() => {
            PerformValidation();
        });
    }

</script>

<template>
    <TextInput
        ref="textInput" 
        :text-value="props.modelValue" 
        @update:textValue="onUpdateValue"
        :maxLength="14"
        :title="props.title"
        :acceptCharacter="LimitToPhoneCharacters"
        :disabled="props.disabled"
        :rightAddOnDisplayStyle="rightAddonDisplayStyle"
        :hideRightAddOn="!indicateBadNumber"
        :validator="(normVal, evt) => { return !badNumber;}"
        @focus="onFocus"
        @blur="emitFormattedText">
        <template #LeftAddon>
            <Icon :icon="IconTypes.Phone" :displayStyle="iconDisplayStyle" />
        </template>
        <template #RightAddon>
            <Icon v-if="badNumber" :icon="IconTypes.Error" :displayStyle="DisplayStyle.Light" title="Invalid phone number: Must be 10 digits." />
        </template>
    </TextInput>
</template>